import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import LikertInputCustom from "./minorComponents/LikertInputCustom";
import AddProblemToSurvey from "../mutations/AddProblemToSurvey";
import GetSurvey from "../queries/GetSurvey";
// reactstrap components
import {
  Table,
  Input,
  FormText,
  Card,
  CardHeader,
  CardBody,
  Button,
  FormGroup,
  Row,
  Col,
} from "reactstrap";
import { useAppContext } from "../AppContext";

const headerStyle = {
  width: "1%",
};

const cellStyle = {
  whiteSpace: "normal",
  maxWidth: "300px",
};

const SurveySectionTwo = ({ survey }) => {
  const { displayToast, closeToast } = useAppContext();
  const params = useParams();
  const [errors, setErrors] = useState([]);
  const [content, setContent] = useState("");
  const [pageNumber, setPageNumber] = useState("");
  const [problemType, setProblemType] = useState(null);
  const [addProblem] = useMutation(AddProblemToSurvey);

  function showToast(success, message) {
    const toast = {
      title: success ? "Success" : "Error",
      color: success ? "bg-success" : "bg-danger",
      icon: success ? "check-bold" : "fat-remove",
      message,
    };

    displayToast(toast);

    setTimeout(() => {
      closeToast();
    }, 10000);
  }

  const handleMutation = (variables) => {
    return addProblem({
      variables,
      refetchQueries: [
        {
          query: GetSurvey,
          variables: { surveyId: params.surveyId },
        },
      ],
    })
      .then((result) => {
        console.log("Mutation result: ", result);
      })
      .catch((error) => {
        console.log("Mutation error:", error);
      });
  };

  function onSubmit() {
    let newErrors = [];

    if (pageNumber === "") newErrors.push({ key: "new_page_number", text: "Please input a page number." });
    if (problemType == null) newErrors.push({ key: "new_problem_type", text: "Please select a type." });
    if (content === "") newErrors.push({ key: "content", text: "Please input a description of the problem" });

    setErrors(newErrors);

    const variables = {
      surveyId: params.surveyId,
      page_number: pageNumber,
      problem_type: problemType,
      content: content,
    };

    if (newErrors.length === 0) {
      handleMutation(variables).then(() => {
        showToast(true, "You have submitted a problem. If you have no additional problems to submit, please select another section at the top of this page.");
      });

      setContent("");
      setProblemType(null);
      setPageNumber("");
    } else {
      showToast(false, "Please ensure problem is reported correctly.");
    }
  }

  function displayProblems() {
    if (survey.problems.length === 0) {
      return (
        <div className='d-flex flex-column p-5 my-5 h-100 w-100 justify-content-center align-items-center'>
          <h1>
            <i className='ni ni-bell-55 text-muted' />
          </h1>
          <h3 className='text-muted'>No problems Logged</h3>
        </div>
      );
    }

    return (
      <Table hover responsive className='survey-two'>
        <thead>
          <tr>
            <th scope='col' style={headerStyle}>
              Page Number
            </th>
            <th scope='col' style={headerStyle}>
              Problem Type
            </th>
            <th scope='col' colSpan={3}>
              Problem Description
            </th>
            {/* <th scope='col' style={headerStyle}>
              Status
            </th> */}
          </tr>
        </thead>
        <tbody>
          {survey.problems.map((problem, pIndex) => {
            return (
              <tr key={`problem_${pIndex}`}>
                <td>{problem.page_number}</td>
                <td>{problem.problem_type.charAt(0).toUpperCase() + problem.problem_type.slice(1)}</td>
                <td colSpan='3' style={cellStyle}>
                  {problem.content}
                </td>
                {/* <td>Pending</td> */}
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  }

  function handleChange(name, newValue) {
    if (name === "problemType") {
      setProblemType(newValue);
    }
  }

  return (
    <div className='pt-3'>
      <h2>Section 2: Technical Problem/Error Log</h2>
      <p>
        <strong>Instructions:</strong> Describe each technical problem or other error/problem you encounter while
        validating this course component (module, assessment, or posttest). Please log each issue <i>separately</i>.
      </p>
      <p>
        <i>Technical problems</i> include any issues with courseware functionality, such as inability to go
        forward/backward within the course using navigation controls; correct/incorrect answers not indicated for an
        activity question; Audio/Visual (A/V) media controls not working, etc. <i>Other Errors</i> to report could
        include problems with the course content, such as mistakes in foreign language characters, missing punctuation,
        or issues with language usage.
      </p>
      <p>
        <strong>Please make an entry only if you encounter an error or issue</strong>; there is no need to report, "No
        errors found."
      </p>
      <p>
        <strong>Note 1:</strong> For the Page Number field below, a number for each page in a module is listed beneath
        the page title at the bottom left of each page (e.g., 1 of 27, 2 of 27, etc.).
      </p>
      <p>
        <strong>Note 2:</strong> After completing your review of this module/assessment, please continue to Section 3 of
        the Survey, General Feedback.
      </p>
      <Row>
        <Col className='order-xl-2 mb-5 mb-xl-0' xl='4'>
          <Card className='bg-secondary shadow'>
            <CardHeader className='bg-white border-0'>
              <Row className='align-items-center'>
                <Col>
                  <h3 className='mb-0'>Add Issue</h3>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <Row>
                <Col lg='6' xl='12'>
                  <FormGroup>
                    <label className='form-control-label' htmlFor='input-title'>
                      Page Number
                    </label>
                    <Input
                      type='number'
                      placeholder='Page Number'
                      className='form-control'
                      value={pageNumber}
                      onChange={(e) => {
                        setPageNumber(Number(e.target.value));
                      }}
                      min={0}
                    />
                    <FormText className='text-red'>
                      {errors.some((error) => error.key === "new_page_number") ? (
                        errors.find((error) => error.key === "new_page_number").text
                      ) : (
                        <></>
                      )}
                    </FormText>
                  </FormGroup>
                  <FormGroup>
                    <label className='form-control-label' htmlFor='input-type'>
                      Problem Type
                    </label>
                    <LikertInputCustom
                      handleChange={(name, newValue) => handleChange(name, newValue)}
                      name={"problemType"}
                      currentValue={problemType}
                      values={[
                        { text: "Technical", value: "technical" },
                        { text: "Other", value: "other" },
                      ]}
                    />
                    <FormText className='text-red'>
                      {errors.some((error) => error.key === "new_problem_type") ? (
                        errors.find((error) => error.key === "new_problem_type").text
                      ) : (
                        <></>
                      )}
                    </FormText>
                  </FormGroup>
                </Col>
                <Col lg='6' xl='12'>
                  <FormGroup>
                    <label className='form-control-label' htmlFor='input-title'>
                      Problem Description
                    </label>
                    <Input
                      type='text'
                      placeholder='Issue'
                      className='form-control'
                      value={content}
                      onChange={(e) => {
                        setContent(e.target.value);
                      }}
                    />
                    <FormText className='text-red'>
                      {errors.some((error) => error.key === "content") ? (
                        errors.find((error) => error.key === "content").text
                      ) : (
                        <></>
                      )}
                    </FormText>
                  </FormGroup>
                </Col>
              </Row>
              <hr className='my-4' />
              <Button
                color='primary'
                onClick={(e) => {
                  e.preventDefault();
                  onSubmit();
                }}
              >
                Submit Problem
              </Button>
            </CardBody>
          </Card>
        </Col>
        <Col className='order-xl-1' xl='8'>
          <Card className='shadow-lg'>
            <CardBody className='p-0'>{displayProblems()}</CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SurveySectionTwo;
